<template>
  <ApolloQuery
    v-if="!skipQuery"
    :query="require('./DayDishesQuery.gql')"
    :variables="{ dayOfWeek: currentDay, cityId: userData.cityId, timezoneOffset: timezoneOffset }"
    fetch-policy="cache-and-network"
    @result="updateCategories"
  >
    <ApolloSubscribeToMore
      :document="require('./DishesSubscriptionQuery.gql')"
      :variables="{ dayOfWeek: currentDay, cityId: userData.cityId }"
      fetch-policy="cache-and-network"
      :update-query="updateQuery"
    />
    <div slot-scope="{ result: { data } }">
      <v-container
        v-touch="{ left: onSwipeLeft, right: onSwipeRight}"
        fluid
        :style="{'padding-left': ($vuetify.breakpoint.smAndDown ? '0px' : '16px'),
                 'padding-right': ($vuetify.breakpoint.smAndDown ? '0px' : '16px')}"
      >
        <div v-if="!data" class="center" :style="{height: '66vh'}">
          <v-progress-circular
            class="vertical-center"
            size="100"
            :width="6"
            indeterminate
            color="primary"
          />
        </div>
        <template v-else-if="categories.length > 0">
          <add-custom-order-button v-if="data.dayDishes && isAdmin" :providers="data.dayDishes.providers" :day-of-week="currentRoute.day" />
          <provider-card
            :key="`providerCard-${providerCardData(data.dayDishes).providerName}`"
            class="mb-4 mx-2"
            transition="slide-x-transition"
            :card-data="providerCardData(data.dayDishes)"
            @right="onSwipeRight"
            @left="onSwipeLeft"
            @disableDishes="disableDishes(data.dayDishes)"
          />
          <dishes-sorting
            :key="`dishesSorting-${providerCardData(data.dayDishes).providerName}`"
            class="mx-2"
            :hide-price="data.dayDishes.isFreeOfCharge"
            @orderingChange="onReorder"
          />
          <dish-categories
            ref="dishCategories"
            :categories="categories"
            :disable-context-menu="data.dayDishes.isFreeOfCharge"
            :disabled="disableDishes(data.dayDishes)"
            :is-free-of-charge="isFreeOfCharge"
            :order-by="orderBy"
            :hide-price="data.dayDishes.isFreeOfCharge"
            :selections-in-cart="selectionsInCart"
            :selections-in-order="selectionsInOrder"
            @addSelection="addSelection"
          />
        </template>
        <template v-else>
          <div :style="{color: 'rgba(0,0,0,.54)', display: 'flex', 'flex-direction': 'column', margin: 'auto', 'align-items': 'center'}">
            <v-icon :style="{'font-size': '13rem'}">local_pizza</v-icon>
            <br>
            <div v-t="'dishesContainer.uhOh'" :style="{'font-size': '3rem'}" />
            <div v-t="'dishesContainer.nothingToEat'" :style="{'font-size': '1.5rem'}" />
          </div>
        </template>
      </v-container>
    </div>
  </ApolloQuery>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import TimeService from '@/services/TimeService';
import NotificationService from '@/services/NotificationService';
import AuthenticationService from '@/services/AuthenticationService';
import ProviderCard from './provider-card/ProviderCard.vue';
import DishesSorting from './dishes-sorting/DishesSorting.vue';
import AddCustomOrderButton from './add-custom-order/AddCustomOrderButton.vue';
import { WeekdayGuard } from '../../router/RouterGuards';
import DishCategories from './dish-categories/DishCategories.vue';


export default {
  name: 'DishesContainer',
  components: {
    'provider-card': ProviderCard,
    'dishes-sorting': DishesSorting,
    'dish-categories': DishCategories,
    'add-custom-order-button': AddCustomOrderButton,
  },
  beforeRouteUpdate: WeekdayGuard,
  data() {
    return {
      activeProviderId: '',
      orderBy: null,
      requestLength: 0,
      sortedSelections: {},
      currentDay: null,
      timezoneOffset: moment().utcOffset(),
      isFreeOfCharge: false,
      isDisabled: null,
    };
  },
  computed: {
    ...mapGetters('drawerStore', ['currentRoute', 'isDrawerOpen']),
    ...mapGetters('userInfoStore', ['userData']),
    ...mapGetters('dishesStore', [
      'activeSelectionsState',
      'confirmedSelections',
      'nonConfirmedSelections',
      'categories',
      'noSelectionMadeYet',
    ]),
    isAdmin() {
      return AuthenticationService.isAdmin();
    },
    skipQuery() {
      return !this.currentRoute.isDishesOverview || !this.currentDay;
    },
  },
  watch: {
    'currentRoute.day': function func(newDay, previousDay) {
      if (this.activeSelectionsState === 'dirty' && !this.noSelectionMadeYet && !this.isDisabled) {
        NotificationService.showUnsavedChangesModal({
          header: 'dishesContainer.submitOrder',
          body: 'dishesContainer.unsavedChanges',
          agreeCallback: () => this.savePreviousSelection(previousDay),
        });
      }
      this.currentDay = newDay || previousDay;
    },
  },
  mounted() {
    this.currentDay = this.currentRoute.day;
  },
  beforeDestroy() {
    if (this.activeSelectionsState === 'dirty' && !this.noSelectionMadeYet && !this.isDisabled) {
      NotificationService.showUnsavedChangesModal({
        header: 'dishesContainer.submitOrder',
        body: 'dishesContainer.unsavedChanges',
        agreeCallback: () => this.saveSelection({ dayOfWeek: this.currentDay }),
      });
    }
  },
  methods: {
    ...mapActions('dishesStore', [
      'addSelection',
      'savePreviousSelection',
      'saveSelection',
      'setCategories',
    ]),
    ...mapActions('drawerStore', ['nextProvider', 'previousProvider']),
    onSwipeRight() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.nextProvider();
      }
    },
    onSwipeLeft() {
      if (this.$vuetify.breakpoint.smAndDown) {
        this.previousProvider();
      }
    },
    onReorder(newOrdering) {
      this.orderBy = newOrdering;
      const ref = this.$refs.dishCategories;
      if (ref) {
        ref.reorderItems(newOrdering);
      }
    },
    selectionsInCart(selection) {
      const result = this.nonConfirmedSelections.filter(s => s.id === selection.id).length;
      return result;
    },
    selectionsInOrder(selection) {
      const result = this.confirmedSelections.filter(s => s.id === selection.id).length;
      return result;
    },
    updateQuery(previousResult, { subscriptionData }) {
      const result = {
        dayDishes: Object.assign({}, previousResult.dayDishes, {
          providers: previousResult.dayDishes
            .providers.map(p => Object.assign({}, p, {
              categories: p.categories.map(c => Object.assign({}, c, {
                selections: c.selections.map(s => Object.assign({}, s, {
                  requestsCount:
                    subscriptionData.data.requestsCount.find(d => d.id === s.id).count,
                  isTopPick: subscriptionData.data.requestsCount
                    .find(d => d.id === s.id).isTopPick,
                })),
              })),
            })),
        }),
      };

      return result;
    },
    activeProvider(day) {
      if (!day) {
        return null;
      }
      const activeProvider = day.providers.find(p => p.name === this.currentRoute.provider) || day.providers[0];
      if (!activeProvider) {
        return null;
      }

      return activeProvider;
    },
    color(day) {
      if (!this.activeProvider(day)) {
        return '';
      }

      return this.activeProvider(day).color;
    },
    providerCardData(day) {
      if (!this.activeProvider(day)) {
        return {};
      }
      return {
        providerName: this.activeProvider(day).name,
        providerColor: this.activeProvider(day).color,
        comment: day.comment,
        orderUntil: day.orderUntil,
        providersCount: day.providers.length,
      };
    },
    disableDishes(day) {
      this.isDisabled = TimeService.IsDateInPast(day.orderUntil);
      return this.isDisabled;
    },
    async updateCategories({ data }) {
      if (data && data.dayDishes && data.dayDishes.providers.length > 0) {
        this.isFreeOfCharge = data.dayDishes.isFreeOfCharge;
        const newProvider = this.activeProvider(data.dayDishes);
        await this.setCategories(newProvider.categories);
        const ref = this.$refs.dishCategories;
        if (ref) {
          if (this.activeProviderId !== newProvider.id) {
            ref.reorderItems(this.orderBy);
          } else {
            ref.updateItems(this.orderBy);
          }
        }
        this.activeProviderId = newProvider.id;
      } else {
        this.setCategories([]);
      }
    },
  },
};
</script>

<style scoped>
.is-loading {
  transition: none !important;
  pointer-events: none;
  cursor: default;
}
</style>
